
import { Code, Cpu, Database, Columns } from 'lucide-react';
import { Card } from './ui/card';
import { useEffect, useState, useRef } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const SkillsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);

  const skills = [
    {
      category: 'Product & Architecture',
      icon: <Columns className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mb-3 sm:mb-4 animate-pulse-slow" />,
      items: ['System Design', 'Micro-frontends', 'Module Federation', 'Performance Optimization', 'Accessibility', 'Team Leadership']
    },
    {
      category: 'Frontend Core',
      icon: <Code className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mb-3 sm:mb-4 animate-pulse-slow" />,
      items: ['TypeScript', 'JavaScript (ES6+)', 'React 18', 'Next.js', 'Redux', 'Zustand', 'Tailwind CSS']
    },
    {
      category: 'DevOps & Testing',
      icon: <Database className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mb-3 sm:mb-4 animate-pulse-slow" />,
      items: ['Webpack / Vite', 'Docker', 'GitHub Actions', 'Vitest', 'React Testing Library', 'AWS (Cognito, S3)']
    },
    {
      category: 'Exploring Next',
      icon: <Cpu className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mb-3 sm:mb-4 animate-pulse-slow" />,
      items: ['Python', 'LLMs', 'RAG', 'Agentic Workflows', 'MCP', 'OpenAI API']
    }
  ];

  return (
    <section id="skills" ref={sectionRef} className="relative overflow-hidden">
      {/* Background vector elements */}
      <div className="vector-bg-elem vector-1"></div>
      <div className="vector-bg-elem vector-2"></div>
      <div className="vector-bg-elem vector-3"></div>
      
      <div className="section-container relative z-10">
        <div className={`mb-8 sm:mb-12 text-center max-w-2xl mx-auto transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0 fade-up' : 'opacity-0 translate-y-8'
        }`}>
          <p className="heading-highlight mb-2 text-base sm:text-lg">My Expertise</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Skills & Technologies</h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            With 5+ years in the industry, I've developed expertise in various frontend technologies.
            Here are my main areas of expertise:
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {skills.map((skillGroup, index) => (
            <Card 
              key={index} 
              className={`p-4 sm:p-6 border border-border/50 bg-card hover:border-accent/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                transitionDelay: `${(index + 1) * 100}ms`,
                animationDelay: `${index * 150}ms`,
                animationName: isVisible ? 'fadeUp' : 'none',
                animationDuration: '0.6s',
                animationFillMode: 'forwards'
              }}
            >
              <div className="flex flex-col h-full">
                {skillGroup.icon}
                <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{skillGroup.category}</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-muted-foreground text-sm">
                  {skillGroup.items.map((skill, skillIndex) => (
                    <li 
                      key={skillIndex} 
                      className="flex items-center gap-2"
                      style={{ 
                        animationDelay: `${(skillIndex * 100) + 300}ms`,
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateX(0)' : 'translateX(-10px)',
                        transition: `opacity 0.3s ease-out ${(skillIndex * 100) + 300}ms, transform 0.3s ease-out ${(skillIndex * 100) + 300}ms`
                      }}
                    >
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent"></span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;

